import { MACHINE_TILE_IMAGE } from '../../demo/demoMode';
import { Checkbox } from '../../components/Checkbox';
import { MobileListItemConfig } from '../../components/data-display/ResponsiveDataView';
import { cn } from '../../utils/className';
import { Machine } from '../../types/machines/machine.types';
import { getMachineLocation, getMachineShortId } from './machineIdentity';
import { MachineStatusBadge } from './MachineStatusBadge';

interface MachineCardProps {
  machine: Machine;
  config: MobileListItemConfig<Machine>;
}

export function MachineCard({ machine, config }: MachineCardProps) {
  const { isSelected, selectionMode, onSelect, onClick, isActive } = config;

  const name = machine.displayName || machine.machineId || 'Unknown';
  const shortId = getMachineShortId(machine);
  const location = getMachineLocation(machine);

  // The checkbox sits over the card's button, not inside it. A control nested
  // in a button is invalid, and a screen reader cannot reach it.
  return (
    <div className='relative h-full w-full min-w-0'>
      <button
        type='button'
        onClick={() => onClick?.(machine)}
        aria-current={isActive ? 'true' : undefined}
        className={cn(
          // A tap focuses the button. Only a keyboard focus draws a ring.
          `group flex h-full w-full min-w-0 cursor-pointer flex-col gap-2
          rounded-lg text-left outline-none`,
          `focus-visible:ring-focus focus-visible:ring-2
          focus-visible:ring-offset-2`
        )}
      >
        <div
          className={cn(
            `bg-surface-focus-support flex h-40 w-full flex-col items-end gap-1
            rounded-lg border border-transparent p-2 transition-colors`,
            'group-hover:border-border-focus',
            isSelected && 'border-border-focus',
            isActive && 'border-border-focus ring-border-focus ring-1'
          )}
        >
          <img
            // The design gives every card the same render, so it says nothing
            // about the machine and stays out of the accessibility tree.
            src={MACHINE_TILE_IMAGE}
            alt=''
            className='min-h-0 w-full flex-1 object-contain'
            loading='lazy'
            decoding='async'
          />
          <MachineStatusBadge status={machine.status} />
        </div>

        <div className='flex w-full min-w-0 flex-col'>
          <h3 className='size-p1 text-focus w-full truncate font-bold'>
            {name}
          </h3>

          {shortId && (
            <span
              className='size-caption text-secondary w-full truncate font-bold'
            >
              {shortId}
            </span>
          )}

          {location && (
            <p className='size-caption text-secondary w-full truncate'>
              {location}
            </p>
          )}
        </div>
      </button>

      {/* Mounted outside selection mode too, so the checkbox can fade in. */}
      <div
        inert={!selectionMode}
        className={cn(
          `absolute top-2 right-2 transition-opacity duration-200 ease-in-out
          motion-reduce:transition-none`,
          selectionMode ? 'opacity-100' : 'opacity-0'
        )}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onSelect?.(machine.id)}
          aria-label={`Select ${name}`}
          className='border-border-primary size-6 rounded-[5px]
            [&_svg]:size-3.5'
        />
      </div>
    </div>
  );
}
